/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react/prop-types */
import { DataGrid, GridLoadingOverlay, GridPagination, GridSortModel } from '@mui/x-data-grid';
import React from 'react';

import CustomGridToolbar from './CustomGridToolbar';
import CustomSummary from './DataGridSummary';

import { Theme } from '@material-ui/core';
import { SxProps } from '@mui/material';
import { isExpired } from 'src/helpers/checkExpiry';
import CustomDataGridNoRowOverlay from './CustomDataGridNoRowOverlay';

interface CustomDatagridProps {
  rows: any[];
  columns: any[];
  rowHeight?: number | 'auto';
  pagination?: boolean;
  summary?: any;
  hiddenCols?: any;
  toolbar?: any;
  loading?: boolean;
  rowSelection?: boolean;
  onEditCellChange?: (params: any) => void;
  pageNumber?: 5 | 10 | 15 | 50 | 100;
  sorting?: GridSortModel;
  canExport?: any;
  canPrint?: any;
  noRowsMessage?: string;
  search?: any;
  footer?: React.ReactNode;
  cellBorder?: boolean;
  isZeroMargin?: boolean;
  headerProps?: any;
  isHeader?: boolean;
  onRowClick?: (params: any) => void;
  apiRef?: any;
  autoHeight?: boolean;
  rowSelectionModel?: any[];
  onRowSelectionModelChange?: (params: any) => void;
  checkboxSelection?: boolean;
  isRowSelectable?: (params: any) => boolean;
  columnVisibilityModel?: any;
  onRowDoubleClick?: (params: any) => void;
  height?: number;
}

const CustomDatagrid: React.FC<CustomDatagridProps> = ({
  rows,
  columns,
  rowHeight,
  pagination,
  summary,
  hiddenCols,
  toolbar,
  loading,
  rowSelection,
  onEditCellChange,
  pageNumber,
  sorting,
  canExport,
  canPrint,
  noRowsMessage,
  search,
  footer,
  cellBorder,
  isZeroMargin,
  headerProps,
  isHeader,
  onRowClick,
  apiRef,
  autoHeight = true,
  checkboxSelection = false,
  onRowSelectionModelChange,
  isRowSelectable,
  columnVisibilityModel,
  rowSelectionModel,
  onRowDoubleClick, // ✅ Add this line
  height = 300,
}: CustomDatagridProps) => {
  const getRowHeight = (): number | 'auto' => rowHeight || 'auto';

  //const getRowClassName = (params: any): string => {
  //  let classNames = params.indexRelativeToCurrentPage % 2 === 0 ? 'rdg-row-even' : 'rdg-row-odd';

  //  classNames += ` ${
  //    Number.isInteger(params.row.status) && (params.row.status === 4 || params.row.status === 6)
  //      ? 'deleted-row-red'
  //      : ''
  //  } ${params.row.group ? 'row-roles-group' : 've'} ${
  //    params.row.inventoryOf === 2 ? 'vehicle-da-inventory' : ''
  //  } ${params?.row?.statusClass ? params?.row?.statusClass : ''} ${
  //    params.row?.disable ? 'row-disable' : ''
  //  } ${params.row?.duplicate ? 'row-duplicate' : ''} ${params.row?.failed ? 'row-failed' : ''}`;

  //  return classNames;
  //};
  const getRowClassName = (params: any): string => {
    const row = params.row;

    const classes = [
      row.isFullRowNotification ? 'notification-full-row' : '',
      params.indexRelativeToCurrentPage % 2 === 0 ? 'rdg-row-even' : 'rdg-row-odd',
      Number.isInteger(row.status) && (row.status === 4 || row.status === 6)
        ? 'deleted-row-red'
        : '',
      row.group ? 'row-roles-group' : 've',
      row.inventoryOf === 2 ? 'vehicle-da-inventory' : '',
      row.statusClass || '',
      row.disable ? 'row-disable' : '',
      row.duplicate ? 'row-duplicate' : '',
      row.failed ? 'row-failed' : '',
    ];

    return classes.filter(Boolean).join(' ');
  };

  const getStausClassName = (status: any) => {
    switch (status) {
      case 1:
        return 'semi-bold-text'; // Apply font-weight: 600 for status 1
      case 2:
        return 'normal'; // Apply bold font-weight for status 2
      case 3:
        return 'bg-rowClr'; // Apply light font-weight for status 3
      case 4:
        // Apply any additional styling for status 4 or return an empty string if no styling is needed
        return 'semi-bold-text'; // Replace with your own class for status 4
      default:
        return ''; // No additional styling for other statuses
    }
  };

  const slotProps: any = {
    panel: {
      sx: {
        top: '-120px !important',
      },
    },
    toolbar: {
      sx: {
        marginBottom: '20px !important',
      },
    },
  };

  const sx: SxProps<Theme> = {
    '& .MuiDataGrid-columnSeparator': {
      display: 'none !important',
    },
    '& .MuiButtonBase-root': {
      backgroundColor: '#0A1A2C',
    },
    '&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell, &.MuiDataGrid-root--densityStandard .MuiDataGrid-cell, &.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell':
      {
        py: isZeroMargin ? '0' : rowHeight === 'auto' ? '15px' : '0.4rem',
        px: isZeroMargin ? '0' : rowHeight === 'auto' ? '15px' : '0.4rem',
        borderRadius: isZeroMargin ? '0.3rem' : '0',
      },
    '& .MuiDataGrid-cell': {
      borderBottom: 'none',
      ...(cellBorder && {
        border: 2,
        borderColor: '#495d73',
        borderRight: 0,
        borderTop: 0,
      }),
    },
    '.css-ltf0zy-MuiDataGrid-iconButtonContainer': {
      visibility: 'hidden !important',
    },
    '.MuiDataGrid-columnHeader': {
      backgroundColor: '#0A1A2C',
      padding: `0px 6px !important`,
    },
    '.MuiCheckbox-root': {
      backgroundColor: 'transparent !important',
    },
    '& .MuiDataGrid-cell, & .MuiDataGrid-columnHeaderTitle': {
      textOverflow: 'ellipsis',
    },
  };

  return (
    <div
      style={{
        width: '100%',
        height: rows.length > 10 ? `calc(100vh - ${height || 300}px)` : 'auto', // 👈 dynamic height
        overflowY: 'auto', // enables scroll
      }}
    >
      <DataGrid
        style={{
          color: 'white',
          borderStyle: 'none',
        }}
        getRowClassName={getRowClassName}
        rows={rows.map((item, index) => ({ id: index + 1, ...item }))}
        getRowHeight={getRowHeight}
        processRowUpdate={onEditCellChange}
        columns={columns}
        autoHeight={rows.length <= 10} // 👈 use autoHeight only for small sets
        checkboxSelection={checkboxSelection}
        disableColumnMenu={true}
        disableRowSelectionOnClick={false}
        apiRef={apiRef}
        rowSelection={rowSelection}
        onRowClick={onRowClick}
        onRowDoubleClick={onRowDoubleClick} // ✅ Add this line
        getRowId={(rows) => rows.id}
        pageSizeOptions={[5, 10, 15, 50, 100]}
        loading={loading}
        isRowSelectable={isRowSelectable}
        columnVisibilityModel={columnVisibilityModel}
        rowSelectionModel={rowSelectionModel}
        onRowSelectionModelChange={onRowSelectionModelChange}
        initialState={{
          pagination: {
            paginationModel: {
              pageSize: pageNumber ? pageNumber : 100,
            },
          },
          columns: hiddenCols,
          sorting: {
            sortModel: sorting,
          },
        }}
        slots={{
          footer: () => (
            <div className="d-flex flex-1 flex-column">
              {footer && footer}
              {summary && <CustomSummary rows={rows} columns={columns} summary={summary} />}
              {pagination && <GridPagination />}
            </div>
          ),
          toolbar: () => (
            <CustomGridToolbar
              canExport={canExport}
              canPrint={canPrint}
              search={search}
              headerProps={headerProps}
              isHeader={isHeader}
            />
          ),
          // noRowsOverlay: () => <CustomDataGridNoRowOverlay message={noRowsMessage || 'No Rows'} />,
          loadingOverlay: () => (
            <GridLoadingOverlay style={{ backgroundColor: 'rgb(0 0 0 / 29%)' }} />
          ),
        }}
        slotProps={slotProps}
        sx={sx}
      />
    </div>
  );
};

export default CustomDatagrid;
